'use client';

/**
 * RecordStep — the CHEAP voice path (story-creation v2): the child/parent just
 * dictates the whole idea, we speech-to-text it via the worker (POST
 * /api/transcriptions → poll), show the transcript in an EDITABLE box, and hand
 * it up as the story prompt. Unlike the agent-guided DictateStep there is no live
 * model in the loop — one STT job, then the standard editable form. A child may
 * ramble; the text is fully editable and the parent finishes the details on the
 * next (parameters) step.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Mic, Square, Loader2, AlertTriangle, Check } from 'lucide-react';
import { useAudioRecorder } from '@/contexts/media/hooks/useAudioRecorder';
import { MicLevelMeter } from '@/contexts/media/components/MicLevelMeter';
import { refineDictation } from '@/contexts/content/actions/refineDictation';
import type { StoryBrief } from '@/contexts/media/realtimeVoice';

/** Approx worst-case hold for a dictation: STT (≤3 min) + one LLM refine pass. */

const TRANSCRIBE_ROUTE = '/api/transcriptions';
const MAX_RECORD_SECONDS = 180;
const POLL_MS = 1500;
const POLL_TIMEOUT_MS = 90_000;

type Phase = 'idle' | 'recording' | 'transcribing' | 'ready' | 'error';

interface RecordStepProps {
  locale: string;
  /** Fired once the parent accepts the transcript. `brief` is the LLM-refined
   *  structured extraction (genre/age/heroes…) when it succeeded; otherwise the
   *  caller falls back to the raw text as the prompt. */
  onComplete: (text: string, brief: StoryBrief | null, mode: RecordMode) => void;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * What to do with the transcript: compose a NEW story from it (the default —
 * generate, brief-prefilled form) or keep it AS IS (a story read aloud goes
 * through from-text/verbatim, no LLM rewrite). Only offered for a transcript
 * long enough to be a story, not an idea.
 */
export type RecordMode = 'compose' | 'verbatim';
const VERBATIM_MIN_CHARS = 100;

export function RecordStep({ locale, onComplete }: RecordStepProps) {
  const t = useTranslations('content.wizard.record');
  const recorder = useAudioRecorder({ maxDurationSeconds: MAX_RECORD_SECONDS });
  const [phase, setPhase] = useState<Phase>('idle');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refining, setRefining] = useState(false);
  const [mode, setMode] = useState<RecordMode>('compose');
  const canVerbatim = text.trim().length >= VERBATIM_MIN_CHARS;

  const proceed = useCallback(async () => {
    const value = text.trim();
    if (value.length < 3) return;
    const chosen: RecordMode = mode === 'verbatim' && value.length >= VERBATIM_MIN_CHARS ? 'verbatim' : 'compose';
    setRefining(true);
    // Best-effort LLM tidy + extract (title/heroes); on failure just use the raw transcript.
    const res = await refineDictation(value, locale).catch(() => null);
    setRefining(false);
    onComplete(value, res && res.success ? res.data : null, chosen);
  }, [text, locale, onComplete, mode]);
  /** the wait for the transcript (pushed) — dropped when the step goes away or records again */
  const waitAbort = useRef<AbortController | null>(null);

  const clearPoll = useCallback(() => {
    waitAbort.current?.abort();
    waitAbort.current = null;
  }, []);
  useEffect(() => () => clearPoll(), [clearPoll]);

  const transcribe = useCallback(
    async (blob: Blob, mimeType: string) => {
      setPhase('transcribing');
      setError(null);
      try {
        const audioBase64 = await blobToBase64(blob);
        const res = await fetch(TRANSCRIBE_ROUTE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audioBase64, mimeType, language: locale }),
        });
        if (res.status === 402) {
          setError(t('insufficientTokens'));
          setPhase('error');
          return;
        }
        if (!res.ok) throw new Error(`transcribe ${res.status}`);
        const { id } = (await res.json()) as { id: string };

        // pushed (transcription.updated), with a fallback only while the stream is down
        const { waitForTranscript } = await import('@/contexts/ai/services/waitForTranscript');
        const aborter = new AbortController();
        waitAbort.current = aborter;
        const row = await waitForTranscript(id, { timeoutMs: POLL_TIMEOUT_MS, fallbackMs: POLL_MS, signal: aborter.signal, route: TRANSCRIBE_ROUTE });
        if (row.status === 'ready' && row.text) {
          setText(row.text.trim());
          setPhase('ready');
        } else {
          setError(t('error'));
          setPhase('error');
        }
      } catch {
        setError(t('error'));
        setPhase('error');
      }
    },
    [locale, t],
  );

  const start = useCallback(async () => {
    setError(null);
    setText('');
    recorder.resetRecording();
    await recorder.startRecording();
    if (recorder.error) {
      setError(t('micDenied'));
      setPhase('error');
      return;
    }
    setPhase('recording');
  }, [recorder, t]);

  // Seamless like every premium op — the server enforces the allowance.
  const requestStart = useCallback(() => {
    void start();
  }, [start]);

  const stop = useCallback(() => {
    recorder.stopRecording();
    // audioBlob lands asynchronously after MediaRecorder flushes — handled below.
  }, [recorder]);

  // Once recording stops and the blob is ready, kick off transcription.
  useEffect(() => {
    if (phase === 'recording' && !recorder.isRecording && recorder.audioBlob) {
      void transcribe(recorder.audioBlob, recorder.mimeType);
    }
  }, [phase, recorder.isRecording, recorder.audioBlob, recorder.mimeType, transcribe]);

  const mins = Math.floor(recorder.duration / 60);
  const secs = recorder.duration % 60;

  return (
    <div data-testid="wizard-step-record" className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-base font-medium">{t('title')}</h3>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      {phase === 'error' && error && (
        <Alert variant="destructive" data-testid="record-error">
          <AlertTriangle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Record / stop control */}
      {phase !== 'ready' && (
        <div className="flex flex-col items-center gap-2 py-2">
          {recorder.isRecording ? (
            <>
              <Button size="lg" variant="destructive" onClick={stop} data-testid="record-stop" className="gap-2">
                <Square className="size-5" />
                {t('stop')} · {mins}:{secs.toString().padStart(2, '0')}
              </Button>
              <MicLevelMeter level={recorder.level} elapsedSeconds={recorder.duration} className="max-w-xs" />
            </>
          ) : (
            <Button
              size="lg"
              onClick={requestStart}
              disabled={phase === 'transcribing'}
              data-testid="record-start"
              className="gap-2"
            >
              {phase === 'transcribing' ? <Loader2 className="size-5 animate-spin" /> : <Mic className="size-5" />}
              {phase === 'transcribing' ? t('transcribing') : t('start')}
            </Button>
          )}
        </div>
      )}

      {/* Editable transcript */}
      {phase === 'ready' && (
        <div className="space-y-3" data-testid="record-transcript">
          <p className="text-sm text-muted-foreground">{t('reviewHint')}</p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            className="text-sm"
            data-testid="record-text"
          />
          {canVerbatim && (
            <div className="space-y-1" data-testid="record-mode">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={mode === 'compose' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMode('compose')}
                  data-testid="record-mode-compose"
                >
                  {t('modeCompose')}
                </Button>
                <Button
                  type="button"
                  variant={mode === 'verbatim' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMode('verbatim')}
                  data-testid="record-mode-verbatim"
                >
                  {t('modeVerbatim')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {mode === 'verbatim' ? t('modeVerbatimHint') : t('modeComposeHint')}
              </p>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={requestStart} data-testid="record-again">
              <Mic className="mr-2 size-4" />
              {t('again')}
            </Button>
            <Button
              onClick={proceed}
              disabled={text.trim().length < 3 || refining}
              data-testid="record-continue"
            >
              {refining ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Check className="mr-2 size-4" />}
              {refining ? t('transcribing') : t('continue')}
            </Button>
          </div>
        </div>
      )}

    </div>
  );
}
